import { DebugInfo } from '@jotta/ui/DebugInfo'
import type { ThemeUIStyleObject } from '@jotta/ui/themeui'
import { useObservableElementRef } from '@jotta/hooks'
import clsx from 'clsx'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import type { FunctionComponent, ReactNode, RefObject } from 'react'
import { useEffect } from 'react'
import { useIntersection } from 'react-use'
import styles from './VirtualList.module.scss'
import { VirtualListRows } from './VirtualListRows'
import type { VirtualListOptions } from './useVirtualList'
import { useVirtualList } from './useVirtualList'
const debug = Debug('jotta:virtual:VirtualList')
export type VirtualListItemFunctionComponent<T> = FunctionComponent<{
  item: T
}>

export interface VirtualListProps<T> {
  options: VirtualListOptions<T>
  renderItem: VirtualListItemFunctionComponent<T>
  onScrollBottomElement?: ReactNode
  listSx?: ThemeUIStyleObject
  children?: ReactNode
  className?: string
  containerRef?: RefObject<HTMLDivElement>
}

export const VirtualList = observer(function VirtualList<T>({
  options,
  renderItem,
  children,
  listSx,
  onScrollBottomElement = null,
  className = '',
  containerRef,
  ...props
}: VirtualListProps<T>) {
  const scrollBottomRef =
    useObservableElementRef<HTMLDivElement>('scrollbottom')
  const state = useVirtualList(options)
  const intersect = useIntersection(scrollBottomRef, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  })
  const { onScrollBottom, showDebugInfo = false } = options
  useEffect(() => {
    if (onScrollBottom && intersect && intersect.isIntersecting) {
      debug('onScrollBottom')
      onScrollBottom()
    }

    return () => {}
  }, [intersect, onScrollBottom])

  return (
    <>
      {showDebugInfo && (
        <DebugInfo
          sx={{
            backgroundColor: 'white',
            position: 'absolute',
            top: '4',
            right: '4',
            zIndex: 4,
          }}
          info={state.debugInfo}
        />
      )}
      <div
        data-testid="VirtualList"
        className={clsx({
          [className]: className,
          [styles.viewport]: true,
        })}
        style={state.cssCustomProperties}
        ref={containerRef}
        {...props}
      >
        {children}
        <VirtualListRows store={state} renderItem={renderItem}>
          <div
            ref={scrollBottomRef}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: 1,
            }}
          >
            {onScrollBottomElement}
          </div>
        </VirtualListRows>
      </div>
    </>
  )
})
